import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { NotificationBar } from '@eneco/sparky';
import type { NotificationBarProps } from '@eneco/sparky/types';

import type { NotificationsBarContent } from './notifications-bar-content.types';

/**
 * `NotificationsBar` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/NotificationsBar/
 * NotificationsBar.tsx`): a single `NotificationBar`, shown only when `showNotificationOption` is set,
 * defaulting to the `warning` variant when none is given.
 *
 * The migration bundle collapsed the single Sitecore `notification` field into one `notifications` array
 * entry (ADR-0068's role-collapse — see `notifications-bar-content.types.ts`); this picks that one entry
 * back out by its `role`, falling back to the raw `notification` field the Sitecore lane sends instead.
 */
const NotificationsBar: FC = () => {
  const { adapter } = useCms();
  const content = useContent<NotificationsBarContent>();

  const notification = content.notifications?.find(entry => entry.role === 'notification') ?? content.notification;

  if (!content.showNotificationOption || !notification) return null;

  const html = adapter.toRichText(notification.content);

  return (
    <NotificationBar
      title={notification.title ?? ''}
      text={html ? <RichTextComponent html={html} /> : undefined}
      variant={(notification.variant as NotificationBarProps['variant']) || 'warning'}
    />
  );
};

export default NotificationsBar;
